<script lang="ts" setup>
import { isAxiosError } from 'axios'
import { cva } from 'class-variance-authority'
import { AlertOutlineIcon, InformationOutlineIcon } from 'mdi-vue3'
import { computed, h, ref } from 'vue'

import { cn } from '../../../../'
import type { ADialogProperties } from '.'
import { ADialog } from '.'

const messageVariants = cva('atabix-message-dialog__container', {
  variants: {
    variant: {
      error: 'atabix-error-dialog__content',
      success: 'atabix-info-dialog__content',
    },
  },
})

export type MessageDialogType = 'error' | 'info' | 'success' | 'warning'

export type AMessageDialogProperties = /* @vue-ignore */ ADialogProperties & {
  type?: MessageDialogType
  description?: string | unknown
  message?: string
}

const properties = withDefaults(defineProps<AMessageDialogProperties>(), {
  actions: undefined,
  cancel: () => ({
    variant: 'outline',
    click: () => {},
  }),
  class: undefined,
  confirm: undefined,
  contentCollapse: undefined,
  description: undefined,
  message: undefined,
  persistent: undefined,
  title: undefined,
  type: undefined,
})

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const computedType = computed((): MessageDialogType => properties.type as any)

const computedTitle = computed((): string => {
  if (properties.title) {
    return properties.title
  }

  if (isAxiosError(Error)) {
    return 'Server error'
  }

  switch (computedType.value) {
    case 'info': {
      return 'Info'
    }
    case 'success': {
      return 'Success'
    }
    case 'warning': {
      return 'Warning'
    }
    default: {
      return 'Error'
    }
  }
})

const computedMessage = computed((): string => {
  const message = properties.message || properties.description

  if (typeof message === 'string') {
    return message
  }

  if (isAxiosError(message)) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return (message as any).response?.data?.message
  }

  switch (computedType.value) {
    case 'info': {
      return 'This is a very valuable info.'
    }
    case 'success': {
      return 'You have created a success!'
    }
    case 'warning': {
      return 'This is the warning sign!'
    }
    // eslint-disable-next-line unicorn/no-useless-switch-case
    case 'error':
    default: {
      return 'An error has occurred!'
    }
  }
})

const computedCancel = computed((): ADialogProperties['cancel'] => {
  return properties.cancel === undefined ? true : properties.cancel
})

const computedStyle = computed(() => {
  return properties.type === 'error' ? cn(messageVariants({ variant: 'error' })) : cn(messageVariants({ variant: 'success' }))
})

const computedIcon = computed(() => {
  switch (properties.type) {
    case 'info': {
      return h(InformationOutlineIcon)
    }
    case 'error': {
      return h(AlertOutlineIcon)
    }
    default: {
      return ''
    }
  }
})

const computedIconClass = computed(() => {
  return properties.type === 'error' ? 'text-foreground-alert' : 'text-foreground-info'
})
// end Props

// Emits
export interface AErrorDialogEmits {
  (event: 'clickOutsideOrKeyupEscape'): void
  (event: 'close'): void
}

const emit = defineEmits<AErrorDialogEmits>()

// end Emits

// Refs
const dialog = ref<typeof ADialog | undefined>(undefined)
// end Refs

// Computed
// end Computed

// Functions
function onClickOutsideOrKeyupEscape(): void {
  emit('clickOutsideOrKeyupEscape')
}

function onHeaderCloseIconClick(): void {
  emit('close')
}
// end Functions

// Async Api functions
// end Async Api functions

// Lifecycle Hooks / Init
// end Lifecycle Hooks / Init
</script>

<template>
  <ADialog
    ref="dialog"
    v-bind="properties"
    :cancel="computedCancel"
    :class="['atabix-message-dialog', computedStyle]"
    :title="computedTitle"
    :type="computedType"
    @click-outside-or-keyup-escape="onClickOutsideOrKeyupEscape"
    @close="onHeaderCloseIconClick"
  >
    <div v-if="message" class="atabix-message-dialog__description-container">
      <div v-if="computedIcon" class="atabix-message-dialog__icon">
        <component :is="computedIcon" :class="computedIconClass" />
      </div>
      <slot name="description">
        <div class="atabix-message-dialog__description">
          {{ computedMessage }}
        </div>
      </slot>
    </div>
  </ADialog>
</template>

<style lang="scss">
.atabix-message-dialog {
  @apply min-w-[576px];
}

.atabix-message-dialog__container {
  @apply items-center;
}

.atabix-message-dialog__description-container {
  @apply flex;
  @apply items-center;
  @apply gap-2;
}

.atabix-message-dialog__description {
  @apply flex;
  @apply justify-center;
}

.atabix-error-dialog__content {
  @apply bg-background-alertLight;
}

.atabix-info-dialog__content {
  @apply bg-background-infoLight;
}

.atabix-message-dialog__icon {
  @apply w-5;
  @apply h-5;
}
</style>
